import React from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Divider,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDexService } from '../../../contexts/DexServiceContext';
import { LogData, LogLevel } from '../../../types/dex';

/**
 * 日志查看器组件
 * 显示DexService的操作日志
 */
const LogViewer: React.FC = () => {
  const { logs, clearLogs } = useDexService();

  // 获取日志图标
  const getLogIcon = (level: LogLevel) => {
    switch (level) {
      case LogLevel.ERROR:
        return <ErrorIcon color="error" />;
      case LogLevel.WARNING:
        return <WarningIcon color="warning" />;
      case LogLevel.SUCCESS:
        return <CheckCircleIcon color="success" />;
      case LogLevel.INFO:
      default:
        return <InfoIcon color="info" />;
    }
  };

  // 获取日志时间格式
  const formatLogTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // 清空日志
  const handleClearLogs = () => {
    clearLogs();
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          操作日志
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={handleClearLogs}
          disabled={logs.length === 0}
        >
          清空日志
        </Button>
      </Box>

      {logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          暂无日志记录
        </Typography>
      ) : (
        <List sx={{ 
          maxHeight: 400, 
          overflow: 'auto',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1
        }}>
          {logs.map((log: LogData, index: number) => (
            <React.Fragment key={log.timestamp}>
              <ListItem
                sx={{
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Tooltip title={log.level}>
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    {getLogIcon(log.level)}
                  </Box>
                </Tooltip>
                <ListItemText
                  primary={log.message}
                  secondary={formatLogTime(log.timestamp)}
                  primaryTypographyProps={{
                    sx: { 
                      wordBreak: 'break-all',
                      color: log.level === LogLevel.ERROR ? 'error.main' : 'text.primary'
                    }
                  }}
                  secondaryTypographyProps={{
                    sx: { color: 'text.secondary' }
                  }}
                />
              </ListItem>
              {index < logs.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default LogViewer;
